/**
 * ONLY · Shop the look (sections/on-shop-the-look.liquid).
 * - El [+] del look (uno por look) abre su pop up: <dialog> modal (foco atrapado, Escape y clic en
 *   el velo cierran, la página no scrollea). Tocar la foto del look también lo abre, sin otra parada
 *   de tabulación. Entra y sale con fundido (Figma: Dissolve 200 ms ease-out) y al cerrarse el foco
 *   vuelve al [+].
 * - «AÑADIR SIZE» despliega los talles: en desktop debajo del botón; en mobile desde abajo, con un
 *   segundo velo que los cierra (Figma 551:33811). Escape con los talles abiertos cierra sólo los
 *   talles; un clic en otro lugar del panel también.
 * - El talle elegido agrega esa variante con /cart/add.js y el evento estándar de Shopify (header y
 *   carrito de Horizon se enteran solos, como en la compra rápida de la tarjeta). Si se agregó, se
 *   cierra el pop up y se abre el carrito (Figma: talle → «Carrito con producto»); si no, el error
 *   de Shopify queda debajo del botón.
 */

import { lockScroll, prefersReducedMotion, unlockScroll } from '@theme/utilities';

const FADE_MS = 250;

/** @param {Element | null} element */
function isLookDialog(element) {
  return element instanceof HTMLDialogElement && element.hasAttribute('data-on-look-dialog');
}

class OnShopTheLook extends HTMLElement {
  /** @type {HTMLElement | null} */
  trigger = null;
  closing = false;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    // cancel y close no burbujean: se escuchan en captura.
    this.addEventListener('cancel', this.onCancel, true);
    this.addEventListener('close', this.onDialogClose, true);
  }

  disconnectedCallback() {
    this.querySelectorAll('dialog[data-on-look-dialog]').forEach((dialog) => unlockScroll(dialog));
  }

  onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // El [+] o, fuera del pop up, cualquier punto de la foto del look (que lleva su [+] adentro).
    const opener = /** @type {HTMLElement | null} */ (
      target.closest('[data-on-look-open]') ??
        (target.closest('dialog') ? null : target.closest('[data-on-look]')?.querySelector('[data-on-look-open]'))
    );
    if (opener) {
      this.open(opener);
      return;
    }

    const dialog = /** @type {HTMLDialogElement | null} */ (target.closest('dialog[data-on-look-dialog]'));
    if (!dialog) return;

    // El <dialog> ocupa toda la ventana: un clic que le llega a él es un clic en el velo.
    if (target === dialog || target.closest('[data-on-look-close]')) {
      this.close(dialog);
      return;
    }

    if (target.closest('[data-on-look-veil]')) {
      this.closeSizes(dialog, true);
      return;
    }

    const toggle = /** @type {HTMLButtonElement | null} */ (target.closest('[data-on-look-toggle]'));
    if (toggle) {
      this.toggleSizes(dialog, toggle);
      return;
    }

    const size = /** @type {HTMLButtonElement | null} */ (target.closest('[data-on-look-size]'));
    if (size) {
      if (!size.disabled) this.add(dialog, size);
      return;
    }

    if (!target.closest('[data-on-look-sizes]')) this.closeSizes(dialog);
  };

  /** @param {HTMLElement} opener */
  open(opener) {
    const dialog = document.getElementById(opener.getAttribute('aria-controls') || '');
    if (!isLookDialog(dialog) || /** @type {HTMLDialogElement} */ (dialog).open) return;
    const modal = /** @type {HTMLDialogElement} */ (dialog);
    this.trigger = opener;
    modal.showModal();
    lockScroll(modal);
    // Un reflow antes de la clase para que el fundido (y el velo) animen.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    /** @type {HTMLElement | null} */ (modal.querySelector('[data-on-look-close]'))?.focus();
  }

  /** @param {HTMLDialogElement} dialog */
  async close(dialog) {
    if (!dialog.open || this.closing) return;
    this.closing = true;
    this.closeSizes(dialog);
    dialog.classList.remove('is-open');
    if (!prefersReducedMotion()) {
      await new Promise((resolve) => {
        const done = () => {
          dialog.removeEventListener('transitionend', onEnd);
          resolve(undefined);
        };
        const onEnd = (/** @type {TransitionEvent} */ event) => {
          if (event.target === dialog && event.propertyName === 'opacity') done();
        };
        dialog.addEventListener('transitionend', onEnd);
        window.setTimeout(done, FADE_MS);
      });
    }
    this.closing = false;
    if (dialog.open) dialog.close();
  }

  /* Limpieza de cualquier cierre (también el que fuerza el navegador). */
  onDialogClose = (/** @type {Event} */ event) => {
    const dialog = /** @type {HTMLDialogElement} */ (event.target);
    if (!isLookDialog(dialog)) return;
    dialog.classList.remove('is-open');
    this.closeSizes(dialog);
    unlockScroll(dialog);
    const trigger = this.trigger?.isConnected
      ? this.trigger
      : /** @type {HTMLElement | null} */ (this.querySelector(`[aria-controls="${dialog.id}"]`));
    trigger?.focus({ preventScroll: true });
    this.trigger = null;
  };

  onCancel = (/** @type {Event} */ event) => {
    const dialog = /** @type {HTMLDialogElement} */ (event.target);
    if (!isLookDialog(dialog)) return;
    event.preventDefault();
    if (this.closeSizes(dialog, true)) return;
    this.close(dialog);
  };

  /**
   * @param {HTMLDialogElement} dialog
   * @param {HTMLButtonElement} toggle
   */
  toggleSizes(dialog, toggle) {
    const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
    this.closeSizes(dialog);
    if (wasOpen) return;
    const sizes = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!sizes) return;
    toggle.setAttribute('aria-expanded', 'true');
    sizes.classList.add('is-open');
    dialog.classList.add('has-sizes');
    /** @type {HTMLButtonElement | null} */ (sizes.querySelector('button:not(:disabled)'))?.focus({ preventScroll: true });
  }

  /**
   * Cierra los talles abiertos del pop up. Devuelve si había alguno abierto.
   * @param {HTMLDialogElement} dialog
   * @param {boolean} [restoreFocus] - devolver el foco a «AÑADIR SIZE»
   */
  closeSizes(dialog, restoreFocus = false) {
    const toggle = /** @type {HTMLButtonElement | null} */ (
      dialog.querySelector('[data-on-look-toggle][aria-expanded="true"]')
    );
    if (!toggle) return false;
    toggle.setAttribute('aria-expanded', 'false');
    document.getElementById(toggle.getAttribute('aria-controls') || '')?.classList.remove('is-open');
    dialog.classList.remove('has-sizes');
    if (restoreFocus) toggle.focus({ preventScroll: true });
    return true;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {HTMLButtonElement} button - talle (o «AÑADIR» de un producto sin talles) con data-variant-id
   */
  async add(dialog, button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const line = button.closest('[data-on-look-line]');
    const status = line?.querySelector('[data-on-look-status]');
    const toggle = /** @type {HTMLButtonElement | null} */ (line?.querySelector('[data-on-look-toggle]') ?? null);
    const setStatus = (/** @type {string} */ text, isError = false) => {
      if (!status) return;
      status.textContent = text;
      status.classList.toggle('is-error', isError);
    };

    const variantId = button.dataset.variantId;
    button.setAttribute('aria-busy', 'true');
    setStatus(this.dataset.adding || '…');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    /** @type {(value: any) => void} */
    let resolveEvent = () => {};
    /** @type {(reason: any) => void} */
    let rejectEvent = () => {};
    const promise = new Promise((resolve, reject) => {
      resolveEvent = resolve;
      rejectEvent = reject;
    });
    promise.catch(() => {});

    const events = await import('@shopify/events').catch(() => null);
    const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
    if (CartLinesUpdateEvent) {
      button.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise,
        })
      );
    }

    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      const productId = line?.getAttribute('data-product-id') ?? undefined;

      if (!response.ok || data.status) {
        // 422: sin stock suficiente u otro motivo; el mensaje de Shopify queda debajo del botón.
        setStatus(data.description || data.message || this.dataset.error || '', true);
        resolveEvent({
          cart: CartLinesUpdateEvent?.createCartFromAjaxResponse?.(cart),
          detail: { didError: true, items: cart.items, source: 'on-shop-the-look', productId },
        });
        this.closeSizes(dialog);
        toggle?.focus({ preventScroll: true });
        return;
      }

      setStatus('');
      resolveEvent({
        cart: CartLinesUpdateEvent?.createCartFromAjaxResponse?.(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: 1,
          source: 'on-shop-the-look',
          productId,
          sections: data.sections,
        },
      });

      await this.close(dialog);
      const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.getElementById('cart-drawer'));
      if (drawer && !drawer.hasAttribute('open')) drawer.open?.();
    } catch (error) {
      setStatus(this.dataset.error || '', true);
      rejectEvent(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('on-shop-the-look')) customElements.define('on-shop-the-look', OnShopTheLook);
